/**
 * Admin Context Provider for admin panel state management
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminApi } from '../services/adminApi';
import { apiClient } from '../apiClient';
import { useAuth } from '../hooks/useAuth';

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  // Check admin status when user changes
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const adminStatus = await adminApi.isCurrentUserAdmin();
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    };

    checkAdminStatus();
  }, [user]);

  // User management functions
  const loadUsers = async (page = 1, limit = 20, search = '') => {
    try {
      const response = await adminApi.getUsers(page, limit, search);
      setUsers(response.users || []);
      return response;
    } catch (error) {
      console.error('Error loading users:', error);
      throw error;
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const response = await adminApi.getUserDetail(userId);
      setSelectedUser(response);
      return response;
    } catch (error) {
      console.error('Error loading user detail:', error);
      throw error;
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const response = await adminApi.updateUser(userId, userData);

      // Update users list if the user is in it
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, ...response } : user
        )
      );

      // Update selected user if it's the same one
      if (selectedUser && selectedUser.user.id === userId) {
        setSelectedUser(prev => ({
          ...prev,
          user: { ...prev.user, ...response }
        }));
      }

      return response;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  };

  const bulkUpdateUsers = async (userIds, updates) => {
    try {
      const response = await adminApi.bulkUpdateUsers(userIds, updates);

      // Refresh users list after bulk update
      await loadUsers();

      return response;
    } catch (error) {
      console.error('Error bulk updating users:', error);
      throw error;
    }
  };

  const updateUserVisibility = async (userId, showOnLeaderboard) => {
    try {
      const response = await apiClient.updateUserVisibility(userId, showOnLeaderboard);

      // Update users list if the user is in it
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, show_on_leaderboard: showOnLeaderboard } : user
        )
      );

      // Update selected user if it's the same one
      if (selectedUser && selectedUser.user.id === userId) {
        setSelectedUser(prev => ({
          ...prev,
          user: { ...prev.user, show_on_leaderboard: showOnLeaderboard }
        }));
      }

      return response;
    } catch (error) {
      console.error('Error updating user visibility:', error);
      throw error;
    }
  };

  // Task management functions
  const loadTasks = async (page = 1, limit = 20) => {
    try {
      const response = await adminApi.getTasks(page, limit);
      setTasks(response.tasks || []);
      return response;
    } catch (error) {
      console.error('Error loading tasks:', error);
      throw error;
    }
  };

  const createTask = async (taskData) => {
    try {
      const response = await adminApi.createTask(taskData);

      // Add new task to the list
      setTasks(prevTasks => [response, ...prevTasks]);

      return response;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  };

  const updateTask = async (taskId, taskData) => {
    try {
      const response = await adminApi.updateTask(taskId, taskData);

      // Update tasks list
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? response : task
        )
      );

      // Update selected task if it's the same one
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask(response);
      }

      return response;
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  // Audit logs functions
  const loadAuditLogs = async (filters = {}) => {
    try {
      const response = await adminApi.getAuditLogs(filters);
      setAuditLogs(response.logs || []);
      return response;
    } catch (error) {
      console.error('Error loading audit logs:', error);
      throw error;
    }
  };

  const value = {
    // State
    isAdmin,
    loading,
    users,
    tasks,
    auditLogs,
    selectedUser,
    selectedTask,

    // Actions
    loadUsers,
    loadUserDetail,
    updateUser,
    bulkUpdateUsers,
    updateUserVisibility,
    loadTasks,
    createTask,
    updateTask,
    loadAuditLogs,
    setSelectedUser,
    setSelectedTask,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};